import {
  DefaultListItem,
  DefaultListItemNonSelectable,
} from "../../../-components/default-list/list-item"
import {
  useMergedRef,
  useHover,
  useInViewport,
  useDisclosure,
} from "@mantine/hooks"
import { useGetAuthenticatedUser } from "../../app/-components/use-authenticated-user"
import { ChatMessagePaper } from "./ChatMessagePaper"
import { PersonProfilePictureAvatar } from "../../app/-components/person-profile-picture-avatar"
import { ActionIcon, CopyButton, Tooltip } from "@mantine/core"
import { IconCheck, IconCopy, IconMessageReply } from "@tabler/icons-react"
import { useTranslation } from "react-i18next"
import useClipboardAvailable from "../../../typed-client/hooks/useClipboardAvailable"
import { ChatPostDto } from "@/orval/gen/model"
import { ActionIconPencil } from "@components/action-icons"
import { UpdateMessageModal } from "./UpdateMessageModal"
import dayjs from "dayjs"
import { useBatchedSimpleUser } from "@/-dataloaders/simple-users"

interface Props extends DefaultListItemNonSelectable {
  message: ChatPostDto
  onInViewportChange?: (inView: boolean) => void
  onReplyButtonClick?: () => void
  showAvatars?: boolean
  showUserName?: boolean
  readOnly: boolean
  showReadIndicator: boolean
}

export function MessageListItem({
  message,
  showAvatars,
  showUserName,
  onInViewportChange,
  onReplyButtonClick,
  readOnly,
  showReadIndicator,
  ...props
}: Props) {
  const { data: user } = useBatchedSimpleUser(message.userId)

  const { t } = useTranslation("app_server")

  const { ref: inViewportRef, inViewport } = useInViewport()

  React.useEffect(() => {
    onInViewportChange && onInViewportChange(inViewport)
  }, [inViewport])

  const { data: authUser } = useGetAuthenticatedUser()

  const { hovered, ref: hoveredRef } = useHover()

  const ref = useMergedRef(inViewportRef, hoveredRef)

  const clipboardAvailable = useClipboardAvailable()
  const isOwnMessage = authUser.userId === user?.userId

  const [
    updateModalOpened,
    { open: openUpdateModal, close: closeUpdateModal },
  ] = useDisclosure()

  const canBeEdited =
    isOwnMessage &&
    !message.isDeleted &&
    dayjs().diff(dayjs(message.timeStamp), "minutes") <= 15

  return (
    <DefaultListItem
      align="start"
      gap={10}
      p={5}
      ref={ref}
      justify="normal"
      style={
        authUser.userId === user?.userId
          ? {
              flexDirection: "row-reverse",
            }
          : undefined
      }
      {...props}
    >
      {showAvatars && !isOwnMessage ? (
        <PersonProfilePictureAvatar
          personId={user?.personId ?? 0}
          name={user?.userName}
        />
      ) : null}
      <ChatMessagePaper
        showTimestamp
        showReadIndicator={showReadIndicator}
        showReplyToMessage
        showUserName={showUserName}
        maw="80%"
        message={message}
      />
      {hovered ? (
        <>
          {!readOnly && onReplyButtonClick && hovered ? (
            <Tooltip label={t("Reply")} withArrow>
              <ActionIcon
                onClick={onReplyButtonClick}
                variant="subtle"
                c="gray.6"
                style={{ pointerEvents: "all" }}
              >
                <IconMessageReply />
              </ActionIcon>
            </Tooltip>
          ) : null}
          {!!message.postText && clipboardAvailable && (
            <CopyButton value={message.postText} timeout={2000}>
              {({ copied, copy }) => (
                <Tooltip
                  label={copied ? t("CopiedToClipboard") : t("CopyToClipboard")}
                  withArrow
                >
                  <ActionIcon
                    style={{ pointerEvents: "all" }}
                    color={copied ? "teal" : "gray"}
                    variant="subtle"
                    c="gray.6"
                    onClick={copy}
                  >
                    {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                  </ActionIcon>
                </Tooltip>
              )}
            </CopyButton>
          )}
          {canBeEdited && (
            <ActionIconPencil
              variant="subtle"
              c="gray.6"
              onClick={openUpdateModal}
            />
          )}
        </>
      ) : null}
      <UpdateMessageModal
        opened={updateModalOpened}
        onClose={closeUpdateModal}
        postId={message.chatPostId}
        message={message.postText}
      />
    </DefaultListItem>
  )
}
